import {
    Component,
    OnInit,
    AfterViewInit,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { nameof } from '@bds/core';
import {
    UntypedFormGroup,
    ValidatorFn,
    ValidationErrors,
    Validators,
    FormGroup,
    FormControl,
} from '@angular/forms';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxDateBoxComponent } from 'devextreme-angular/ui/date-box';
import { ReportParameterModel } from '../../models/report-parameters.model';

@Component({
    selector: 'rt-report-parameters',
    templateUrl: './rt-report-parameters.component.html',
    styleUrls: ['./rt-report-parameters.component.css'],
})
export class RtReportParametersComponent implements OnInit, AfterViewInit {
    reportForm: UntypedFormGroup;

    @ViewChild('startDateBox') startDateBox: DxDateBoxComponent;
    @ViewChildren(DxValidatorComponent) dxValidators: QueryList<DxValidatorComponent>;

    @Input() controlNames: string[] = ['Begin Date', 'End Date'];
    @Input() matStyle: 'fill' | 'outline' | 'standard' | 'legacy' = 'fill';
    @Output() getReport: EventEmitter<any> = new EventEmitter();
    @Output() cancelReport: EventEmitter<any> = new EventEmitter();

    constructor() {}

    get dxMatStyle(): string {
        switch (this.matStyle) {
            case 'fill':
                return 'filled';
            case 'outline':
                return 'outlined';
            case 'standard':
                return 'underlined';
            default:
                return 'underlined';
        }
    }

    ngOnInit(): void {
        this.reportForm = this.getFormGroup();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.startDateBox?.instance?.focus();
        });
    }

    onDateChanged(controlName: string) {
        const ctrl = this.reportForm.get(controlName);
        if (ctrl?.value) {
            const corrected = correctSerializedYear(ctrl.value);
            if (corrected !== ctrl.value) {
                ctrl.setValue(corrected, { emitEvent: false });
            }
        }
        this.reportForm.updateValueAndValidity();
    }

    getFormGroup(): FormGroup {
        const formGroup = new FormGroup({});
        formGroup.addControl(
            nameof<ReportParameterModel>('startDate'),
            new FormControl('', [Validators.required]),
        );
        formGroup.addControl(
            nameof<ReportParameterModel>('endDate'),
            new FormControl('', [Validators.required]),
        );
        formGroup.setValidators([dateRangeValidator]);

        formGroup.get('startDate').valueChanges.subscribe(() => {
            formGroup.updateValueAndValidity({ emitEvent: false });
        });
        formGroup.get('endDate').valueChanges.subscribe(() => {
            formGroup.updateValueAndValidity({ emitEvent: false });
        });

        return formGroup;
    }

    onGetReportClick() {
        this.reportForm.get('startDate').updateValueAndValidity();
        this.reportForm.get('endDate').updateValueAndValidity();

        this.reportForm.updateValueAndValidity();

        Object.keys(this.reportForm.controls).forEach((key) => {
            this.reportForm.controls[key].markAsTouched();
        });

        if (this.reportForm.valid) {
            this.getReport.emit({
                startDate: this.reportForm.controls.startDate.value,
                endDate: this.reportForm.controls.endDate.value,
            });
        }
    }

    onCancelClick() {
        this.reportForm.reset();
        this.reportForm.markAsPristine();
        this.reportForm.markAsUntouched();
        Object.keys(this.reportForm.controls).forEach((key) => {
            this.reportForm.controls[key].markAsPristine();
            this.reportForm.controls[key].markAsUntouched();
            this.reportForm.controls[key].setErrors(null);
        });
        this.reportForm.setErrors(null);

        this.dxValidators?.forEach((v) => v.instance?.reset());

        this.cancelReport.emit();
    }
}

export function correctSerializedYear(value: string): string {
    if (!value || typeof value !== 'string') {
        return value;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return value;
    }

    const serializedYear = parseInt(match[1], 10);
    const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;

    if (serializedYear >= currentCentury && serializedYear < currentCentury + 100) {
        return value;
    }

    const correctedYear = currentCentury + (serializedYear % 100);
    return `${correctedYear}-${match[2]}-${match[3]}`;
}

export const dateRangeValidator: ValidatorFn = (
    formGrp: UntypedFormGroup,
): ValidationErrors | null => {
    const fromCtrl = formGrp.get('startDate');
    const toCtrl = formGrp.get('endDate');

    if (!fromCtrl?.value || !toCtrl?.value) {
        return null;
    }

    const fromStr = typeof fromCtrl.value === 'string' ? fromCtrl.value.trim() : '';
    const toStr = typeof toCtrl.value === 'string' ? toCtrl.value.trim() : '';

    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!datePattern.test(fromStr) || !datePattern.test(toStr)) {
        return null;
    }

    return fromStr > toStr ? { dateRangeInvalid: true } : null;
};
